<template>
  <div class="cal">
    <!-- 툴바 -->
    <div class="toolbar">
      <div class="nav">
        <button class="ghost" @click="move(-1)">‹</button>
        <button class="ghost today-btn" @click="goToday">오늘</button>
        <button class="ghost" @click="move(1)">›</button>
        <h2 class="label">{{ headerLabel }}</h2>
      </div>
      <div class="right">
        <!-- 태그 필터 — 보고 싶은 태그만 골라서 볼 수 있어요 -->
        <div class="tagfilter">
          <button class="ghost filter-btn" :class="{ on: tagFilter.length }" @click="filterOpen = !filterOpen" title="태그 필터">
            🏷 {{ filterLabel }}
            <span class="caret">▾</span>
          </button>
          <div v-if="filterOpen" class="filter-backdrop" @click="filterOpen = false"></div>
          <div v-if="filterOpen" class="filter-menu">
            <button class="filter-item" :class="{ sel: !tagFilter.length }" @click="clearTagFilter">
              <span class="fi-check">{{ tagFilter.length ? '' : '✓' }}</span>
              <span class="fi-name">전체 보기</span>
            </button>
            <div class="filter-sep"></div>
            <button
              v-for="t in tags" :key="t.id"
              class="filter-item" :class="{ sel: isTagOn(t.id) }"
              @click="toggleTag(t.id)"
            >
              <span class="fi-check">{{ isTagOn(t.id) ? '✓' : '' }}</span>
              <span class="fi-dot" :style="{ background: t.color }">{{ t.emoji }}</span>
              <span class="fi-name">{{ t.name }}</span>
            </button>
            <button class="filter-item" :class="{ sel: isTagOn(0) }" @click="toggleTag(0)">
              <span class="fi-check">{{ isTagOn(0) ? '✓' : '' }}</span>
              <span class="fi-dot none"></span>
              <span class="fi-name">태그 없음</span>
            </button>
            <p v-if="!tags.length" class="filter-empty">설정에서 태그를 먼저 만들어 보세요.</p>
          </div>
        </div>
        <div class="viewswitch">
          <button :class="{ active: view === 'month' }" @click="setView('month')">월</button>
          <button :class="{ active: view === 'week' }" @click="setView('week')">주</button>
          <button :class="{ active: view === 'day' }" @click="setView('day')">일</button>
        </div>
        <button class="primary add" @click="openNew(todayStr)">+ 일정</button>
      </div>
    </div>

    <div v-if="loading" class="loading">불러오는 중…</div>

    <!-- 월간 보기 -->
    <div v-else-if="view === 'month'" class="month">
      <div class="weekhead">
        <div v-for="(w, i) in weekdays" :key="w" :class="{ sun: i === 0, sat: i === 6 }">{{ w }}</div>
      </div>
      <div class="grid" :class="{ selecting: select.active }">
        <div
          v-for="(cell, idx) in cells"
          :key="cell.key"
          class="cell"
          :class="{ othermonth: !cell.inMonth, today: cell.dayStr === todayStr, selected: isSelected(idx), 'has-events': cell.events.length }"
          :data-idx="idx"
          @pointerdown="onCellDown(idx, $event)"
          @contextmenu.prevent
        >
          <div class="cell-top">
            <div class="daynum" :class="{ sun: cell.dow === 0, sat: cell.dow === 6, holiday: cell.holidays.length }">
              {{ cell.date.getDate() }}
            </div>
            <span v-if="cell.holidays.length" class="holiday-name" :title="cell.holidays.join(', ')">
              {{ cell.holidays.join(', ') }}
            </span>
          </div>

          <!-- 넓은 화면: 칩(제목) 표시 -->
          <div class="events chips">
            <div
              v-for="ev in cell.events.slice(0, 3)"
              :key="ev.id"
              class="chip"
              :class="{ allday: ev.all_day }"
              :style="chipStyle(ev)"
              @pointerdown.stop
              @click.stop="openDetail(ev)"
            >
              <span v-if="!ev.all_day" class="chip-time">{{ timeOf(ev.start_at) }}</span>
              <span v-if="evEmoji(ev)" class="chip-emoji">{{ evEmoji(ev) }}</span>
              {{ ev.title }}
            </div>
            <div v-if="cell.events.length > 3" class="more">+{{ cell.events.length - 3 }}개 더</div>
          </div>

          <!-- 좁은 화면: 점(또는 이모지 배지) 표시 (탭하면 데이 시트로 확인) -->
          <div v-if="cell.events.length" class="events dots">
            <span
              v-for="ev in cell.events.slice(0, 4)"
              :key="'d-' + ev.id"
              class="ev-dot"
              :class="{ emoji: evEmoji(ev) }"
              :style="evEmoji(ev) ? { '--dc': evColor(ev) } : { background: evColor(ev) }"
            >{{ evEmoji(ev) }}</span>
            <span v-if="cell.events.length > 4" class="dots-more">+{{ cell.events.length - 4 }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 주간 / 일간 보기 (아젠다 형식) -->
    <div v-else class="agenda">
      <div v-for="day in agendaDays" :key="day.dayStr" class="agenda-day" :class="{ today: day.dayStr === todayStr }">
        <div class="agenda-date" @click="openNew(day.dayStr)">
          <span class="dd" :class="{ holiday: day.holidays.length }">{{ day.date.getDate() }}</span>
          <span class="dw">{{ weekdays[day.dow] }}</span>
        </div>
        <div class="agenda-events">
          <div v-for="h in day.holidays" :key="'h-' + h" class="agenda-holiday">🎌 {{ h }}</div>
          <div v-if="day.events.length === 0" class="empty">일정 없음</div>
          <div
            v-for="ev in day.events"
            :key="ev.id"
            class="agenda-item"
            @click="openDetail(ev)"
          >
            <span class="ev-bar" :style="{ background: evColor(ev) }"></span>
            <span class="t">{{ ev.all_day ? '하루종일' : (timeOf(ev.start_at) + '–' + timeOf(ev.end_at)) }}</span>
            <span class="ti"><span v-if="evEmoji(ev)" class="ti-emoji">{{ evEmoji(ev) }}</span>{{ ev.title }}</span>
            <span class="lo" v-if="ev.location">· {{ ev.location }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 데이 시트 — 날짜를 탭하면 그 날 일정을 확인/추가 -->
    <div v-if="peek.open" class="peek-backdrop" @click.self="closePeek">
      <div class="peek">
        <header class="peek-head">
          <div class="peek-date">
            <span class="pd-day" :class="{ sun: peek.dow === 0, sat: peek.dow === 6, holiday: peek.holidays.length }">
              {{ peek.date.getDate() }}
            </span>
            <div class="pd-meta">
              <div class="pd-full">{{ peekLabel }}</div>
              <div class="pd-dow">{{ weekdays[peek.dow] }}요일</div>
            </div>
          </div>
          <button class="ghost close" @click="closePeek">✕</button>
        </header>
        <div class="peek-body">
          <div v-for="h in peek.holidays" :key="'ph-' + h" class="peek-holiday">🎌 {{ h }}</div>
          <div v-if="!peek.events.length && !peek.holidays.length" class="peek-empty">
            등록된 일정이 없어요
          </div>
          <button
            v-for="ev in peek.events"
            :key="'pe-' + ev.id"
            class="peek-item"
            @click="openDetail(ev)"
          >
            <span class="pi-bar" :style="{ background: evColor(ev) }"></span>
            <span class="pi-time">{{ ev.all_day ? '하루종일' : timeOf(ev.start_at) + '–' + timeOf(ev.end_at) }}</span>
            <span class="pi-title"><span v-if="evEmoji(ev)" class="pi-emoji">{{ evEmoji(ev) }}</span>{{ ev.title }}</span>
            <span class="pi-loc" v-if="ev.location">{{ ev.location }}</span>
          </button>
        </div>
        <footer class="peek-foot">
          <button class="primary block" @click="openNew(peek.dayStr)">+ 이 날 일정 추가</button>
        </footer>
      </div>
    </div>

    <EventModal
      v-if="modal.open"
      :event="modal.event"
      :default-date="modal.date"
      :default-end-date="modal.endDate"
      @close="closeModal"
      @saved="onSaved"
      @deleted="onDeleted"
    />
  </div>
</template>

<script>
import api from '../api';
import { connectRealtime } from '../lib/realtime';
import EventModal from '../components/EventModal.vue';
import {
  ymd, parseYmd, addDays, startOfWeek, monthGrid,
  WEEKDAYS, splitDateTime, eventsOnDay,
} from '../lib/dateUtils';

const TAG_FILTER_KEY = 'cal.tagFilter'; // 태그 필터 선택은 브라우저에 기억시켜 둔다

export default {
  name: 'CalendarView',
  components: { EventModal },
  data() {
    return {
      view: 'month',
      cursor: new Date(), // 현재 보고 있는 기준 날짜
      todayStr: ymd(new Date()),
      events: [],
      holidays: [],
      loading: true,
      weekdays: WEEKDAYS,
      modal: { open: false, event: null, date: '', endDate: '' },
      // 길게 누르기 → 드래그로 연속 일자 선택
      select: { active: false, anchorIdx: null, currentIdx: null },
      pressTimer: null,
      // 날짜를 짧게 탭하면 그 날 일정을 확인/추가하는 데이 시트
      peek: { open: false, dayStr: '', date: new Date(), dow: 0, events: [], holidays: [] },
      tags: [],           // 내 태그 목록 (필터 메뉴용)
      tagFilter: [],      // 선택된 태그 id 배열 (0 = 태그 없음, 빈 배열 = 전체 보기)
      filterOpen: false,  // 태그 필터 드롭다운 표시 여부
    };
  },
  computed: {
    range() {
      // 현재 뷰가 보여줄 [start, end] (YYYY-MM-DD)
      if (this.view === 'month') {
        const cells = monthGrid(this.cursor);
        return { start: ymd(cells[0]), end: ymd(cells[41]) };
      }
      if (this.view === 'week') {
        const s = startOfWeek(this.cursor);
        return { start: ymd(s), end: ymd(addDays(s, 6)) };
      }
      return { start: ymd(this.cursor), end: ymd(this.cursor) };
    },
    headerLabel() {
      const d = this.cursor;
      if (this.view === 'month') return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
      if (this.view === 'week') {
        const s = startOfWeek(d), e = addDays(s, 6);
        return `${s.getMonth() + 1}.${s.getDate()} – ${e.getMonth() + 1}.${e.getDate()}`;
      }
      return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} (${WEEKDAYS[d.getDay()]})`;
    },
    filterLabel() {
      if (!this.tagFilter.length) return '전체 태그';
      if (this.tagFilter.length === 1) {
        const id = this.tagFilter[0];
        if (id === 0) return '태그 없음';
        const t = this.tags.find((x) => x.id === id);
        return t ? `${t.emoji || ''}${t.name}` : '태그 1개';
      }
      return `태그 ${this.tagFilter.length}개`;
    },
    // 태그 필터가 걸려 있으면 해당 태그 일정만 (0 = 태그 없는 일정)
    filteredEvents() {
      if (!this.tagFilter.length) return this.events;
      return this.events.filter((ev) => this.tagFilter.includes(ev.tag_id || 0));
    },
    peekLabel() {
      const d = this.peek.date;
      return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
    },
    holidayMap() {
      // 'YYYY-MM-DD' → ['공휴일명', ...]
      const m = {};
      for (const h of this.holidays) {
        (m[h.date] = m[h.date] || []).push(h.name);
      }
      return m;
    },
    cells() {
      const monthIdx = this.cursor.getMonth();
      return monthGrid(this.cursor).map((date) => {
        const dayStr = ymd(date);
        return {
          key: dayStr,
          date,
          dayStr,
          dow: date.getDay(), // 일=0
          inMonth: date.getMonth() === monthIdx,
          events: eventsOnDay(this.filteredEvents, dayStr),
          holidays: this.holidayMap[dayStr] || [],
        };
      });
    },
    agendaDays() {
      const days = this.view === 'week'
        ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(this.cursor), i))
        : [this.cursor];
      return days.map((date) => {
        const dayStr = ymd(date);
        return {
          date, dayStr,
          dow: date.getDay(), // 일=0
          events: eventsOnDay(this.filteredEvents, dayStr).sort((a, b) => a.start_at.localeCompare(b.start_at)),
          holidays: this.holidayMap[dayStr] || [],
        };
      });
    },
  },
  watch: {
    view() { this.load(); },
  },
  async created() {
    // 서버 기준 오늘로 보정 (타임존 정합성)
    try {
      const t = await api.today();
      this.todayStr = t.today;
    } catch (e) { /* 무시하고 로컬 기준 사용 */ }
    await this.load();
    await this.loadTags(); // 태그 필터 목록 (+ 저장된 필터 복원)

    // 실시간: 서버 변경 알림 수신 → 현재 보고 있는 범위만 조용히 재조회
    // (활동 로그 표시는 설정 화면에서 담당)
    this._rt = connectRealtime((msg) => {
      if (msg && msg.type === 'event:changed') this.scheduleRealtimeRefresh();
    });
  },
  unmounted() {
    this.clearPressTimer();
    this.endPress();
    if (this._rt) this._rt.close();
    clearTimeout(this._rtTimer);
  },
  methods: {
    timeOf(s) { return splitDateTime(s).time; },
    // 일정 색상 — 태그가 있으면 태그 색, 없으면 (구버전) 커스텀 색, 그것도 없으면 기본 강조색
    evColor(ev) { return (ev && ev.tag && ev.tag.color) || (ev && ev.color) || 'var(--accent)'; },
    // 일정 이모지 — 태그에 이모지가 있을 때만
    evEmoji(ev) { return (ev && ev.tag && ev.tag.emoji) || ''; },
    // 월간 칩 스타일 — 하루종일은 채움, 시간 일정은 옅은 배경 + 좌측 컬러 바
    chipStyle(ev) {
      const c = this.evColor(ev);
      return ev.all_day
        ? { background: c, borderLeftColor: 'transparent', color: '#fff' }
        : { borderLeftColor: c, background: `color-mix(in srgb, ${c} 15%, transparent)` };
    },
    async load() {
      this.loading = true;
      try {
        const { start, end } = this.range;
        const [evRes, holRes] = await Promise.all([
          api.listEvents(start, end),
          api.listHolidays(start, end).catch(() => ({ holidays: [] })),
        ]);
        this.events = evRes.events;
        this.holidays = holRes.holidays || [];
      } catch (e) {
        if (e.status === 401) { window.location.href = api.loginUrl('/'); return; }
        alert('일정을 불러오지 못했습니다: ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    // 태그 목록 로드 + 저장해둔 필터 복원 (지워진 태그는 필터에서 제외)
    async loadTags() {
      try {
        this.tags = (await api.listTags()).tags || [];
      } catch (e) { this.tags = []; }
      try {
        const saved = JSON.parse(localStorage.getItem(TAG_FILTER_KEY) || '[]');
        const valid = new Set([0, ...this.tags.map((t) => t.id)]);
        this.tagFilter = Array.isArray(saved) ? saved.filter((id) => valid.has(id)) : [];
      } catch (e) { this.tagFilter = []; }
    },
    isTagOn(id) { return this.tagFilter.includes(id); },
    toggleTag(id) {
      this.tagFilter = this.isTagOn(id)
        ? this.tagFilter.filter((x) => x !== id)
        : [...this.tagFilter, id];
      this.saveTagFilter();
    },
    clearTagFilter() {
      this.tagFilter = [];
      this.saveTagFilter();
      this.filterOpen = false;
    },
    saveTagFilter() {
      try { localStorage.setItem(TAG_FILTER_KEY, JSON.stringify(this.tagFilter)); } catch (e) { /* 무시 */ }
    },
    // 실시간 변경 반영 — 짧게 디바운스해 연속 변경을 한 번에 처리 (스피너 없이 조용히)
    scheduleRealtimeRefresh() {
      clearTimeout(this._rtTimer);
      this._rtTimer = setTimeout(() => this.reloadEvents(), 150);
    },
    async reloadEvents() {
      try {
        const { start, end } = this.range;
        const res = await api.listEvents(start, end);
        this.events = res.events;
      } catch (e) {
        // 조용히 무시 — 다음 변경 알림이나 수동 새로고침에 반영됨
      }
    },
    setView(v) { this.view = v; },
    move(dir) {
      const d = new Date(this.cursor);
      if (this.view === 'month') d.setMonth(d.getMonth() + dir);
      else if (this.view === 'week') d.setDate(d.getDate() + 7 * dir);
      else d.setDate(d.getDate() + dir);
      this.cursor = d;
      this.load();
    },
    goToday() { this.cursor = parseYmd(this.todayStr); this.load(); },

    // ── 길게 누르기 + 드래그 선택 (월간 보기) ──────────────
    isSelected(idx) {
      const { active, anchorIdx, currentIdx } = this.select;
      if (!active || anchorIdx == null || currentIdx == null) return false;
      return idx >= Math.min(anchorIdx, currentIdx) && idx <= Math.max(anchorIdx, currentIdx);
    },
    onCellDown(idx, e) {
      if (e.button != null && e.button !== 0) return; // 좌클릭/터치만
      this._press = { idx, x: e.clientX, y: e.clientY };
      this.clearPressTimer();
      this.pressTimer = setTimeout(() => {
        this.pressTimer = null;
        this.select = { active: true, anchorIdx: idx, currentIdx: idx };
        if (navigator.vibrate) navigator.vibrate(15);
      }, 350);
      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerCancel);
    },
    onPointerMove(e) {
      if (!this._press) return;
      if (!this.select.active) {
        // 대기 중 손가락이 움직이면(=드래그 의도) 350ms 안 기다리고 바로 선택 시작
        if (Math.abs(e.clientX - this._press.x) > 8 || Math.abs(e.clientY - this._press.y) > 8) {
          this.clearPressTimer();
          this.select = { active: true, anchorIdx: this._press.idx, currentIdx: this._press.idx };
        } else {
          return;
        }
      }
      // 선택 활성 상태: 포인터 밑의 셀을 찾아 범위 확장
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cellEl = el && el.closest('.cell[data-idx]');
      if (cellEl) this.select.currentIdx = Number(cellEl.dataset.idx);
      e.preventDefault();
    },
    onPointerUp() {
      const { active, anchorIdx, currentIdx } = this.select;
      const press = this._press;
      this.clearPressTimer();
      this.endPress();
      if (!active) {
        // 짧은 클릭 → 그 날 일정을 확인/추가하는 데이 시트 열기
        if (press) this.openDayPeek(press.idx);
        return;
      }
      const lo = Math.min(anchorIdx, currentIdx), hi = Math.max(anchorIdx, currentIdx);
      const startStr = this.cells[lo].dayStr;
      const endStr = this.cells[hi].dayStr;
      this.select = { active: false, anchorIdx: null, currentIdx: null };
      this.openNew(startStr, endStr);
    },
    onPointerCancel() {
      this.clearPressTimer();
      this.endPress();
      this.select = { active: false, anchorIdx: null, currentIdx: null };
    },
    clearPressTimer() {
      if (this.pressTimer) { clearTimeout(this.pressTimer); this.pressTimer = null; }
    },
    endPress() {
      this._press = null;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerCancel);
    },

    // 데이 시트 열기 (해당 셀의 일정을 시간순 정렬해 표시)
    openDayPeek(idx) {
      const c = this.cells[idx];
      const events = [...c.events].sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
      this.peek = { open: true, dayStr: c.dayStr, date: c.date, dow: c.dow, events, holidays: c.holidays };
    },
    closePeek() { this.peek.open = false; },
    openNew(dayStr, endStr) {
      this.peek.open = false;
      this.modal = { open: true, event: null, date: dayStr, endDate: endStr || dayStr };
    },
    openDetail(ev) {
      this.peek.open = false;
      this.modal = { open: true, event: ev, date: '', endDate: '' };
    },
    closeModal() { this.modal.open = false; },
    onSaved() { this.closeModal(); this.load(); },
    onDeleted() { this.closeModal(); this.load(); },
  },
};
</script>

<style scoped>
/* 태그 필터 (툴바) */
.tagfilter { position: relative; }
.filter-btn {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; max-width: 160px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.filter-btn.on { color: var(--accent); border-color: var(--accent); background: var(--accent-weak); }
.filter-btn .caret { font-size: 10px; color: var(--muted); }
.filter-backdrop { position: fixed; inset: 0; z-index: 30; }
.filter-menu {
  position: absolute; top: calc(100% + 6px); right: 0; z-index: 31;
  min-width: 200px; max-height: 60vh; overflow-y: auto;
  background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
  box-shadow: var(--shadow); padding: 6px;
  animation: menu-pop 0.14s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
@keyframes menu-pop { from { opacity: 0; transform: translateY(-4px) scale(0.98); } }
.filter-item {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 7px 8px; border: none; border-radius: 8px; background: none;
  font-size: 13px; text-align: left; cursor: pointer; color: var(--text);
}
.filter-item:hover { background: var(--surface-2); }
.filter-item.sel { color: var(--accent); font-weight: 700; }
.fi-check { flex: 0 0 14px; font-size: 12px; color: var(--accent); }
.fi-dot {
  flex: 0 0 auto; width: 18px; height: 18px; border-radius: 50%;
  display: grid; place-items: center; font-size: 11px; line-height: 1; color: #fff;
}
.fi-dot.none { background: transparent; border: 1px dashed var(--border); }
.fi-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filter-sep { height: 1px; margin: 4px 2px; background: var(--border); }
.filter-empty { margin: 4px 8px 6px; font-size: 12px; color: var(--muted); }

.toolbar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 12px; margin-bottom: 16px;
}
.nav { display: flex; align-items: center; gap: 6px; }
.nav .ghost { width: 34px; height: 34px; padding: 0; font-size: 20px; line-height: 1; border-radius: 9px; color: var(--muted); }
.today-btn { width: auto !important; padding: 0 12px !important; font-size: 13px !important; font-weight: 600; }
.label { margin: 0 0 0 10px; font-size: 20px; font-weight: 800; letter-spacing: -0.02em; }
.right { display: flex; align-items: center; gap: 10px; }
.viewswitch {
  display: inline-flex; padding: 3px; gap: 2px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 11px;
}
.viewswitch button {
  border: none; border-radius: 8px; padding: 6px 13px; font-size: 13px; font-weight: 600;
  background: transparent; color: var(--muted);
}
.viewswitch button:hover { background: transparent; color: var(--text); }
.viewswitch button.active {
  background: var(--surface); color: var(--accent);
  box-shadow: var(--shadow-sm); border: 1px solid var(--border-soft);
}
.add { font-weight: 600; }
.loading { padding: 60px; text-align: center; color: var(--muted); }

/* 월간 */
.weekhead {
  display: grid; grid-template-columns: repeat(7, 1fr); text-align: center;
  font-size: 12px; font-weight: 600; color: var(--muted); padding: 2px 0 8px;
}
.weekhead .sat { color: #3b82f6; }
.weekhead .sun { color: var(--danger); }
.grid {
  display: grid; grid-template-columns: repeat(7, 1fr);
  /* 모든 행을 동일한 고정 높이로 — 내용에 따라 칸 높이가 달라지지 않게 */
  grid-auto-rows: 112px;
  border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden;
  background: var(--surface); box-shadow: var(--shadow-sm);
}
.cell {
  border-right: 1px solid var(--border-soft); border-bottom: 1px solid var(--border-soft);
  padding: 6px 6px 5px; cursor: pointer; position: relative;
  /* min-width/height:0 — 긴 일정 제목이 칸(1fr) 폭을 밀지 않고 안에서 잘리도록 */
  min-width: 0; min-height: 0; overflow: hidden;
  display: flex; flex-direction: column;
  transition: background 0.12s ease;
  user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
  /* 셀 위 터치는 브라우저 스크롤 대신 길게누르기+드래그 선택으로 처리 */
  touch-action: none;
}
@media (hover: hover) { .cell:hover { background: var(--surface-2); } }
.grid.selecting .cell { cursor: crosshair; }
/* 선택된 연속 일자 = 하나의 긴 막대처럼 강조 */
.cell.selected { background: var(--accent-weak); box-shadow: inset 0 0 0 2px var(--accent); z-index: 1; }
.cell.selected.today { background: color-mix(in srgb, var(--accent-weak) 70%, var(--today)); }
.cell:nth-child(7n) { border-right: none; }
.cell.othermonth { background: color-mix(in srgb, var(--bg) 55%, transparent); }
.cell.othermonth .daynum { color: var(--muted); opacity: 0.55; }
.cell.today { background: var(--today); }
.cell-top { display: flex; align-items: center; gap: 5px; margin-bottom: 4px; min-height: 22px; }
.daynum {
  font-size: 13px; font-weight: 700; line-height: 22px;
  min-width: 22px; text-align: center; flex-shrink: 0;
}
.cell.today .daynum {
  background: var(--today-ring); color: #fff; border-radius: 50%;
  width: 22px; height: 22px; line-height: 22px;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.4);
}
.daynum.sat { color: #3b82f6; }
.daynum.sun { color: var(--danger); }
.daynum.holiday { color: var(--danger); }
.cell.today .daynum.sat, .cell.today .daynum.sun, .cell.today .daynum.holiday { color: #fff; }
.holiday-name {
  font-size: 11px; color: var(--danger); font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.events { display: flex; flex: 1; min-width: 0; min-height: 0; overflow: hidden; }
.chips { flex-direction: column; gap: 3px; }
.chip {
  font-size: 11px; padding: 2px 7px; border-radius: 6px;
  background: var(--accent-weak); color: var(--text); font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  border-left: 3px solid var(--accent);
  transition: filter 0.12s ease;
}
.chip:hover { filter: brightness(0.96); }
.chip.allday { background: var(--accent); color: #fff; border-left-color: transparent; }
.chip-time { opacity: 0.65; margin-right: 3px; font-variant-numeric: tabular-nums; }
.chip-emoji { margin-right: 2px; }
.more { font-size: 11px; color: var(--muted); font-weight: 600; padding-left: 4px; }

/* 좁은 화면 점(또는 이모지 배지) 표시 (기본은 숨김) */
.dots { display: none; flex-wrap: wrap; align-content: flex-start; align-items: center; gap: 5px; padding-top: 1px; }
.ev-dot {
  width: 9px; height: 9px; border-radius: 50%; background: var(--accent); flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 7px; line-height: 1;
}
/* 태그에 이모지가 있으면 진한 사각 배지 대신, 은은하게 색이 비치는 원형 배지로 표시 */
.ev-dot.emoji {
  background: color-mix(in srgb, var(--dc, var(--accent)) 22%, var(--surface));
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--dc, var(--accent)) 55%, transparent);
}
.dots-more { font-size: 10px; color: var(--muted); font-weight: 700; }

/* 데이 시트 — 날짜 탭 시 뜨는 일정 확인/추가 패널 */
@keyframes fade { from { opacity: 0; } }
@keyframes pop { from { opacity: 0; transform: translateY(8px) scale(0.98); } }
.peek-backdrop {
  position: fixed; inset: 0; z-index: 60;
  background: rgba(12, 16, 24, 0.5); backdrop-filter: blur(3px);
  display: grid; place-items: center; padding: 16px;
  animation: fade 0.16s ease;
}
.peek {
  background: var(--surface); border: 1px solid var(--border); border-radius: 18px;
  box-shadow: var(--shadow); width: 100%; max-width: 420px; max-height: 78vh;
  display: flex; flex-direction: column; overflow: hidden;
  animation: pop 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
.peek-head { display: flex; align-items: center; gap: 12px; padding: 16px 18px 14px; border-bottom: 1px solid var(--border-soft); }
.peek-date { display: flex; align-items: center; gap: 12px; }
.pd-day {
  font-size: 26px; font-weight: 800; line-height: 1; letter-spacing: -0.02em;
  min-width: 30px; text-align: center;
}
.pd-day.sun, .pd-day.holiday { color: var(--danger); }
.pd-day.sat { color: #3b82f6; }
.pd-meta { display: flex; flex-direction: column; gap: 2px; }
.pd-full { font-size: 14px; font-weight: 700; }
.pd-dow { font-size: 12px; color: var(--muted); }
.peek-head .close { margin-left: auto; color: var(--muted); }
.peek-body { padding: 10px 14px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; }
.peek-holiday { font-size: 13px; font-weight: 600; color: var(--danger); padding: 6px 8px; }
.peek-empty { padding: 28px 8px; text-align: center; color: var(--muted); font-size: 13px; }
.peek-item {
  display: grid; grid-template-columns: auto auto 1fr; align-items: center; gap: 4px 10px;
  text-align: left; width: 100%; padding: 11px 12px; border: 1px solid var(--border-soft);
  border-radius: 12px; background: var(--surface-2);
}
.peek-item:hover { background: var(--accent-weak); border-color: transparent; }
.pi-bar { grid-row: 1 / span 2; width: 4px; align-self: stretch; border-radius: 4px; background: var(--accent); }
.pi-time { font-size: 12px; color: var(--accent); font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.pi-title { font-size: 14px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pi-emoji, .ti-emoji { margin-right: 4px; }
.pi-loc { grid-column: 2 / span 2; font-size: 12px; color: var(--muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.peek-foot { padding: 12px 14px; border-top: 1px solid var(--border-soft); }
.block { width: 100%; padding: 11px; font-size: 14px; }

/* 아젠다 (주/일) */
.agenda { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-sm); }
.agenda-day { display: flex; gap: 14px; padding: 14px 16px; border-bottom: 1px solid var(--border-soft); }
.agenda-day:last-child { border-bottom: none; }
.agenda-day.today { background: var(--today); }
.agenda-date { min-width: 48px; text-align: center; cursor: pointer; padding-top: 2px; }
.agenda-date .dd { display: block; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
.agenda-date .dd.holiday { color: var(--danger); }
.agenda-date .dw { font-size: 12px; color: var(--muted); font-weight: 600; }
.agenda-holiday { font-size: 13px; font-weight: 600; color: var(--danger); padding: 3px 2px; }
.agenda-events { flex: 1; display: flex; flex-direction: column; gap: 7px; min-width: 0; }
.agenda-item {
  display: flex; gap: 10px; align-items: center; padding: 10px 12px;
  border-radius: 10px; background: var(--surface-2); border: 1px solid var(--border-soft);
  cursor: pointer; transition: background 0.12s ease, border-color 0.12s ease;
}
.agenda-item:hover { background: var(--accent-weak); border-color: transparent; }
.agenda-item .ev-bar { flex: 0 0 auto; width: 4px; align-self: stretch; border-radius: 4px; }
.agenda-item .t { font-size: 12px; color: var(--muted); font-weight: 600; min-width: 88px; font-variant-numeric: tabular-nums; }
.agenda-item .ti { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agenda-item .lo { color: var(--muted); font-size: 13px; }
.empty { color: var(--muted); font-size: 13px; padding: 8px 2px; }

@media (max-width: 640px) {
  .grid { grid-auto-rows: 74px; box-shadow: none; }
  .cell { padding: 4px 3px; }
  .cell-top { margin-bottom: 2px; min-height: 20px; }
  .daynum { font-size: 12px; line-height: 20px; }
  .cell.today .daynum { width: 20px; height: 20px; line-height: 20px; min-width: 20px; }
  .holiday-name { display: none; }
  /* 좁은 화면: 칩(제목) 숨기고 점으로 */
  .chips { display: none; }
  .dots { display: flex; }
  .label { font-size: 18px; margin-left: 6px; }
  .right { gap: 8px; }
  .add { padding: 8px 12px; }
  .peek { max-width: 100%; align-self: flex-end; border-radius: 20px 20px 0 0; max-height: 80vh; }
  .peek-backdrop { place-items: end center; padding: 0; }
}
</style>
